import { useEffect, useRef, useCallback } from 'react';

/**
 * Generic polling hook with visibility-aware pausing.
 *
 * - Calls `callback` every `intervalMs` while `enabled` and tab is visible.
 * - Pauses when the tab is hidden; resumes and fires immediately on return.
 * - `reset()` restarts the timer (useful after a manual refresh).
 */
export function usePolling(
  callback: () => void,
  intervalMs: number,
  enabled: boolean = true
): { reset: () => void } {
  const savedCallback = useRef(callback);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (!enabled) return;
    intervalRef.current = setInterval(() => {
      savedCallback.current();
    }, intervalMs);
  }, [clearTimer, enabled, intervalMs]);

  const reset = useCallback(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    startTimer();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        savedCallback.current();
        startTimer();
      } else {
        clearTimer();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, startTimer, clearTimer]);

  return { reset };
}
